import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useNavigate } from "react-router-dom";
import FeedRow from "./FeedRow";
import QuickAddBar from "./QuickAddBar";
import {
  ALL_EMPTY_TEXT,
  FeedFilter,
  FeedItem,
  FeedType,
  INACTIVE_DAYS,
  MAGENTA,
  REFERRAL_MAX_DAYS,
  cardSx,
  feedTypeMeta,
  referralDaysInPipeline,
} from "./tokens";

interface PriorityFeedProps {
  items: FeedItem[];
  counts: Record<FeedType, number>;
  filter: FeedFilter;
  onQuickAdd: (title: string) => Promise<boolean>;
  adding: boolean;
  loading: boolean;
  onComplete: (id: string) => void;
  onSnooze: (id: string, days: number) => void;
  onEdit: (reminder: any) => void;
  onDelete: (id: string) => void;
  // GET /dashboard/today caps the inactive lane at 50 rows while counts stay
  // uncapped — surface that so the truncation is never silent.
  inactiveCapped?: boolean;
  // Referrals the server excluded from the bounded lane (started
  // >= REFERRAL_MAX_DAYS ago). Reachable from the referrals lane only, via the
  // My Referrals page.
  olderCounts?: { referrals: number };
  // Inactive-lane sort lives in EngagementsDashboard (flipping it swaps the
  // rows for a server page, see there); the feed only renders the toggle.
  inactiveSort?: "desc" | "asc";
  onToggleInactiveSort?: () => void;
  inactiveSortLoading?: boolean;
}

// "Days … ↓" (largest first) / "Days … ↑" (smallest first) toggle used above
// the right-hand column of the referrals and inactive lanes.
const SortToggle: React.FC<{
  label: string;
  direction: "desc" | "asc";
  onToggle?: () => void;
}> = ({ label, direction, onToggle }) => (
  <Button
    size="small"
    onClick={onToggle}
    aria-label={`Sort by ${label.toLowerCase()}, ${
      direction === "desc" ? "largest" : "smallest"
    } first`}
    endIcon={
      direction === "desc" ? (
        <ArrowDownwardIcon sx={{ fontSize: "15px !important" }} />
      ) : (
        <ArrowUpwardIcon sx={{ fontSize: "15px !important" }} />
      )
    }
    sx={{
      textTransform: "none",
      fontWeight: 700,
      fontSize: "12px",
      color: "#55555c",
      whiteSpace: "nowrap",
      "&:hover": { backgroundColor: "#f1f1f4" },
    }}
  >
    {label}
  </Button>
);

const PriorityFeed: React.FC<PriorityFeedProps> = ({
  items,
  counts,
  filter,
  onQuickAdd,
  adding,
  loading,
  onComplete,
  onSnooze,
  onEdit,
  onDelete,
  inactiveCapped,
  olderCounts,
  inactiveSort = "desc",
  onToggleInactiveSort,
  inactiveSortLoading,
}) => {
  const navigate = useNavigate();

  // Days-in-pipeline sort for the referrals lane. Items arrive longest-first
  // from EngagementsDashboard; the toggle above the column flips that.
  const [referralSort, setReferralSort] = useState<"desc" | "asc">("desc");

  const filtered =
    filter === "all" ? items : items.filter((i) => i.type === filter);
  const visible =
    filter === "referrals"
      ? [...filtered].sort((a, b) => {
          const d = referralDaysInPipeline(b.raw) - referralDaysInPipeline(a.raw);
          return referralSort === "desc" ? d : -d;
        })
      : filtered;
  const total = items.length;

  const intakesShown = visible.filter((i) => i.type === "intakes").length;
  const hiddenIntakes =
    intakesShown > 0 ? Math.max(0, counts.intakes - intakesShown) : 0;

  const inactiveShown = visible.filter((i) => i.type === "inactive").length;
  const inactiveTruncated =
    inactiveShown > 0 && (Boolean(inactiveCapped) || counts.inactive > inactiveShown);

  // Lane filtering lives on the MetricTiles above the card; the header title
  // is what reflects the active lane.
  const headerTitle =
    filter === "all" ? "Today" : `Today - ${feedTypeMeta[filter].chipLabel}`;

  const emptyText =
    filter === "all" ? ALL_EMPTY_TEXT : feedTypeMeta[filter].emptyText;

  // The referrals lane is bounded server-side; anything past the bound is
  // only reachable from the My Referrals page. Show the escape hatch on the
  // referrals lane only — never on "all", where it would be ambiguous.
  const olderReferrals = olderCounts?.referrals || 0;
  const olderAction: { label: string; href: string; accent: string } | null =
    filter === "referrals" && olderReferrals > 0
      ? {
          label: `View ${olderReferrals} older referral${
            olderReferrals === 1 ? "" : "s"
          }`,
          href: `/dashboard/myreferrals?minDays=${REFERRAL_MAX_DAYS}`,
          accent: feedTypeMeta.referrals.accent,
        }
      : null;

  return (
    <Box sx={cardSx}>
      <Box
        sx={{
          padding: "16px 18px 14px",
          borderBottom: "1px solid #efeff1",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box
            sx={{
              width: 6,
              height: 22,
              borderRadius: "3px",
              backgroundColor: MAGENTA,
            }}
          />
          <Box>
            <Typography
              sx={{ fontSize: "17px", fontWeight: 800, color: "#26262b" }}
            >
              {headerTitle}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: "#8a8a90" }}>
              {loading
                ? "Loading…"
                : `${total} item${total === 1 ? "" : "s"} across your engagements`}
            </Typography>
          </Box>
        </Box>

        <QuickAddBar adding={adding} onSubmit={onQuickAdd} />
      </Box>

      {/* Lane toolbar — referrals and inactive only, so the header (and the
          quick-add's position) is identical on every tab. Left: the "older
          referrals" escape hatch (referrals only). Right: a days sort toggle,
          aligned over the "Nd in pipeline" / "Nd inactive" column of the rows
          below. Default is largest number first for both. */}
      {(olderAction || filter === "inactive") && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "8px",
            padding: "8px 16px",
            borderBottom: "1px solid #efeff1",
            backgroundColor: "#fcfcfd",
          }}
        >
          {olderAction ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(olderAction.href)}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                fontSize: "12.5px",
                borderRadius: "8px",
                whiteSpace: "nowrap",
                color: olderAction.accent,
                borderColor: olderAction.accent,
                "&:hover": {
                  borderColor: olderAction.accent,
                  backgroundColor: `${olderAction.accent}14`,
                },
              }}
            >
              {olderAction.label}
            </Button>
          ) : (
            <Box />
          )}
          {filter === "referrals" && (
            <SortToggle
              label="Days in pipeline"
              direction={referralSort}
              onToggle={() =>
                setReferralSort((s) => (s === "desc" ? "asc" : "desc"))
              }
            />
          )}
          {filter === "inactive" && (
            <SortToggle
              label={inactiveSortLoading ? "Days inactive…" : "Days inactive"}
              direction={inactiveSort}
              onToggle={onToggleInactiveSort}
            />
          )}
        </Box>
      )}

      <Box sx={{ maxHeight: "620px", overflowY: "auto" }}>
        {visible.length === 0 ? (
          <Box sx={{ padding: "44px 20px", textAlign: "center" }}>
            <Typography sx={{ fontSize: "34px", lineHeight: 1, color: "#cfcfd4" }}>
              ✓
            </Typography>
            <Typography sx={{ fontSize: "14px", color: "#7a7a80", mt: 1 }}>
              {loading ? "Loading…" : emptyText}
            </Typography>
          </Box>
        ) : (
          <>
            {visible.map((item) => (
              <FeedRow
                key={item.id}
                item={item}
                onComplete={onComplete}
                onSnooze={onSnooze}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
            {/* The server sends at most 5 intake previews but an org-wide count,
                so the Web Intakes tile can read higher than the rows listed. */}
            {hiddenIntakes > 0 && (
              <Typography
                onClick={() => navigate("/dashboard/webIntakeForms")}
                sx={{
                  padding: "11px 16px",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: feedTypeMeta.intakes.accent,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#faf8fa" },
                }}
              >
                + {hiddenIntakes} more unassigned intake
                {hiddenIntakes === 1 ? "" : "s"}
              </Typography>
            )}
            {/* The inactive lane is capped at 50 rows server-side while the
                count stays uncapped — link out to the full paginated page. */}
            {inactiveTruncated && (
              <Typography
                onClick={() =>
                  navigate(
                    `/dashboard/inactive?inactiveDays=${INACTIVE_DAYS}`,
                  )
                }
                sx={{
                  padding: "11px 16px",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: feedTypeMeta.inactive.accent,
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#faf8fa" },
                }}
              >
                Showing {inactiveShown} of {counts.inactive} inactive clients —
                view all
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(PriorityFeed);
